import React, { useEffect, useMemo, useState } from "react";

import { ArrowDown, ArrowUp, ArrowUpDown, FileText } from "lucide-react";

import Pagination from "../Pagination";

import "./style.scss";

type SortOrder = "asc" | "desc";
type SortState = { key: string; order: SortOrder } | null;

interface TableColumn {
  key: string;
  title: string | React.ReactNode;
  width?: string;
  render?: (value: any, record: any) => React.ReactNode;
  textalign?: "left" | "center" | "right";
  className?: string;
  sortable?: boolean;
  sortValue?: (record: any) => any;
  sorter?: (a: any, b: any) => number;
}

interface TableProps {
  columns: TableColumn[];
  data: any[];
  className?: string;
  loading?: boolean;
  // optional expandable row support
  expandedRowRender?: (record: any, index: number) => React.ReactNode;
  rowKey?: (record: any, index: number) => string | number;
  expandedRowKeys?: Array<string | number>;
  onExpandToggle?: (key: string | number, record: any) => void;
  // fixed header with scrollable body
  scrollable?: boolean;
  maxHeight?: string | number;
  // optional pagination
  pagination?: boolean;
  defaultRowsPerPage?: number;
  rowsPerPageOptions?: number[];
  // optional sorting
  sortable?: boolean;
  defaultSort?: SortState;
  sortState?: SortState;
  onSortChange?: (next: SortState) => void;
  /** Keeps the first column visible during horizontal scroll */
  stickyFirstColumn?: boolean;
  /** Keeps the last column visible during horizontal scroll */
  stickyLastColumn?: boolean;
  /** Custom content when data is empty (replaces default empty state) */
  emptyContent?: React.ReactNode;
  /** When true, hides the horizontal scrollbar (default: visible) */
  hideScrollbar?: boolean;
  /** Honor column widths so stacked tables share the same column edges. */
  fixedLayout?: boolean;
}

const Table: React.FC<TableProps> = ({
  columns,
  data,
  className = "",
  loading = false,
  expandedRowRender,
  rowKey,
  expandedRowKeys = [],
  scrollable = false,
  maxHeight = "400px",
  pagination = false,
  defaultRowsPerPage = 10,
  rowsPerPageOptions = [5, 10, 20, 50, 100],
  sortable = false,
  defaultSort = null,
  sortState,
  onSortChange,
  stickyFirstColumn = false,
  stickyLastColumn = false,
  emptyContent,
  hideScrollbar = false,
  fixedLayout = false,
}) => {
  const lastColIndex = columns.length - 1;
  const isStickyFirstCol = (colIndex: number) =>
    stickyFirstColumn && colIndex === 0;
  const isStickyLastCol = (colIndex: number) =>
    stickyLastColumn && colIndex === lastColIndex;
  const stickyColClass = (colIndex: number) => {
    if (isStickyFirstCol(colIndex)) return "table-sticky-col";
    if (isStickyLastCol(colIndex)) return "table-sticky-col-last";
    return "";
  };
  const stickyColHeaderClass = (colIndex: number) => {
    if (isStickyFirstCol(colIndex)) return "table-sticky-col-header";
    if (isStickyLastCol(colIndex)) return "table-sticky-col-last-header";
    return "";
  };
  const stickyColStyle = (colIndex: number, column: TableColumn) => {
    if (isStickyFirstCol(colIndex)) {
      const width = column.width || "200px";
      return { minWidth: width, width, maxWidth: width };
    }
    if (isStickyLastCol(colIndex)) {
      const width = column.width || "220px";
      return { minWidth: width, width, maxWidth: width };
    }
    if (column.width) {
      return { minWidth: column.width, width: column.width };
    }
    return {};
  };
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);
  const [internalSort, setInternalSort] = useState<SortState>(
    sortable ? defaultSort : null
  );

  const effectiveSort: SortState = sortState ?? internalSort;

  const maxHeightValue =
    typeof maxHeight === "number" ? `${maxHeight}px` : maxHeight;

  const sortedData = useMemo(() => {
    if (!sortable || !effectiveSort) return data;

    const col = columns.find(c => c.key === effectiveSort.key);
    if (!col || (col.sortable === false && !col.sorter)) return data;

    const orderMultiplier = effectiveSort.order === "asc" ? 1 : -1;

    const getValue = (record: any) =>
      col.sortValue ? col.sortValue(record) : record?.[col.key];

    const compareDefault = (a: any, b: any) => {
      if (a == null && b == null) return 0;
      if (a == null) return 1;
      if (b == null) return -1;

      if (typeof a === "number" && typeof b === "number") return a - b;
      if (a instanceof Date && b instanceof Date)
        return a.getTime() - b.getTime();

      const as = String(a).toLowerCase();
      const bs = String(b).toLowerCase();
      return as.localeCompare(bs);
    };

    const result = [...data].sort((ra, rb) => {
      if (col.sorter) return col.sorter(ra, rb) * orderMultiplier;
      return compareDefault(getValue(ra), getValue(rb)) * orderMultiplier;
    });

    return result;
  }, [columns, data, effectiveSort, sortable]);

  // Calculate paginated data (after sorting)
  const paginatedData = useMemo(() => {
    if (!pagination) return sortedData;
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return sortedData.slice(startIndex, endIndex);
  }, [sortedData, currentPage, rowsPerPage, pagination]);

  // Reset to page 1 when rows per page changes
  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1);
  };

  // Reset to page 1 when data changes
  useEffect(() => {
    if (pagination) {
      setCurrentPage(1);
    }
  }, [sortedData.length, pagination]);

  useEffect(() => {
    if (!sortable) {
      setInternalSort(null);
      return;
    }
    // If sort is controlled, don't mirror it into internal state.
    if (sortState !== undefined) return;
    setInternalSort(defaultSort);
  }, [defaultSort, sortable, sortState]);

  const cycleSort = (columnKey: string): SortState => {
    const current = effectiveSort;
    if (!current || current.key !== columnKey)
      return { key: columnKey, order: "asc" };
    if (current.order === "asc") return { key: columnKey, order: "desc" };
    return null;
  };

  const handleSortToggle = (column: TableColumn) => {
    if (!sortable) return;
    const isColumnSortable = column.sorter != null || column.sortable === true;
    if (!isColumnSortable) return;

    const next = cycleSort(column.key);
    onSortChange?.(next);
    if (sortState === undefined) setInternalSort(next);
    if (pagination) setCurrentPage(1);
  };

  const renderSortIcon = (column: TableColumn) => {
    if (!sortable) return null;
    const isColumnSortable = column.sorter != null || column.sortable === true;
    if (!isColumnSortable) return null;

    const active =
      effectiveSort?.key === column.key ? effectiveSort.order : null;
    if (active === "asc")
      return <ArrowUp className="w-3.5 h-3.5 text-gray-700" />;
    if (active === "desc")
      return <ArrowDown className="w-3.5 h-3.5 text-gray-700" />;
    return <ArrowUpDown className="w-3.5 h-3.5 text-gray-400" />;
  };

  const renderTableBody = () => {
    const displayData = pagination ? paginatedData : sortedData;
    const startIndex = pagination ? (currentPage - 1) * rowsPerPage : 0;

    return (
      <tbody className="bg-white">
        {loading ? (
          <tr>
            <td colSpan={columns.length} className="px-4 py-12">
              <div className="flex flex-col items-center justify-center min-h-[200px]">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
                <p className="text-sm font-medium text-gray-600 mt-2">
                  Loading...
                </p>
              </div>
            </td>
          </tr>
        ) : sortedData.length === 0 ? (
          <tr>
            <td colSpan={columns.length} className="px-4 py-12">
              <div
                className={`flex flex-col items-center justify-center min-h-[200px]${
                  emptyContent ? " text-center text-gray-500" : ""
                }`}
              >
                {emptyContent ?? (
                  <>
                    <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                      <FileText className="w-8 h-8 text-gray-400" />
                    </div>
                    <p className="text-sm font-medium text-gray-600">
                      No data found
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      There are no records to display
                    </p>
                  </>
                )}
              </div>
            </td>
          </tr>
        ) : (
          displayData.map((record, index) => {
            const actualIndex = startIndex + index;
            const key = rowKey ? rowKey(record, actualIndex) : actualIndex;
            const isExpanded =
              expandedRowRender && expandedRowKeys?.includes(key);
            return (
              <React.Fragment key={String(key)}>
                <tr
                  className={`hover:bg-gray-50 transition-colors duration-150 group ${
                    record.rowClassName !== undefined
                      ? record.rowClassName
                      : "border-b"
                  }`}
                >
                  {columns.map((column, colIndex) => (
                    <td
                      key={column.key}
                      style={{
                        width: column.width,
                        minWidth: column.width,
                        ...stickyColStyle(colIndex, column),
                      }}
                      className={`px-4 py-4 text-sm text-gray-900 ${
                        fixedLayout ? "overflow-hidden" : ""
                      } ${stickyColClass(colIndex)} ${column.className || ""} ${
                        column.textalign === "center"
                          ? "text-center"
                          : column.textalign === "right"
                            ? "text-right"
                            : "text-left"
                      }`}
                    >
                      {column.render
                        ? column.render(record[column.key], record)
                        : record[column.key]}
                    </td>
                  ))}
                </tr>
                {isExpanded && (
                  <tr className="bg-gray-50/30">
                    <td colSpan={columns.length} className="px-4 py-4">
                      <div className="border-l-4 border-purple-500 pl-4">
                        {expandedRowRender?.(record, actualIndex)}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })
        )}
      </tbody>
    );
  };

  if (scrollable) {
    return (
      <div className={`w-full ${className}`}>
        <div
          className={`border-gray-100 bg-white shadow-sm ${
            hideScrollbar ? "overflow-hidden" : "overflow-visible"
          }`}
        >
          <div
            style={{ maxHeight: maxHeightValue }}
            className={`overflow-y-auto overflow-x-auto ${
              hideScrollbar ? "scrollbar-hide" : "table-horizontal-scroll"
            } ${stickyFirstColumn || stickyLastColumn ? "relative isolate" : ""}`}
          >
            <table
              className={`w-full border-collapse ${
                fixedLayout ? "table-fixed" : ""
              } ${
                stickyFirstColumn || stickyLastColumn
                  ? "table-with-sticky-cols"
                  : ""
              }`}
            >
              {fixedLayout ? (
                <colgroup>
                  {columns.map(column => (
                    <col key={column.key} style={{ width: column.width }} />
                  ))}
                </colgroup>
              ) : null}
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  {columns.map((column, colIndex) => (
                    <th
                      key={column.key}
                      style={{
                        width: column.width,
                        minWidth: column.width,
                        ...stickyColStyle(colIndex, column),
                      }}
                      onClick={() => handleSortToggle(column)}
                      className={`table-sticky-header px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200 ${stickyColClass(colIndex)} ${stickyColHeaderClass(colIndex)} ${
                        column.textalign === "center"
                          ? "text-center"
                          : column.textalign === "right"
                            ? "text-right"
                            : "text-left"
                      } ${sortable && (column.sorter != null || column.sortable) ? "cursor-pointer select-none" : ""}`}
                    >
                      <div
                        className={`flex items-center gap-2 ${
                          column.textalign === "center"
                            ? "justify-center"
                            : column.textalign === "right"
                              ? "justify-end"
                              : ""
                        }`}
                      >
                        <span>{column.title}</span>
                        {renderSortIcon(column)}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              {renderTableBody()}
            </table>
          </div>
        </div>
        {pagination && (
          <Pagination
            mode="static"
            currentPage={currentPage}
            pageSize={rowsPerPage}
            dataLength={sortedData.length}
            onPageChange={setCurrentPage}
            onPageSizeChange={handleRowsPerPageChange}
            pageSizeOptions={rowsPerPageOptions}
            className="mt-4"
          />
        )}
      </div>
    );
  }

  return (
    <div className={`w-full ${className}`}>
      <div
        className={`overflow-x-auto border-gray-100 bg-white ${
          hideScrollbar ? "scrollbar-hide" : "table-horizontal-scroll"
        } ${stickyFirstColumn || stickyLastColumn ? "relative isolate" : ""}`}
      >
        <table
          className={`w-full border-collapse ${
            fixedLayout ? "table-fixed" : ""
          } ${
            stickyFirstColumn || stickyLastColumn
              ? "table-with-sticky-cols"
              : ""
          }`}
          style={
            stickyFirstColumn || stickyLastColumn
              ? { minWidth: "max-content" }
              : undefined
          }
        >
          {fixedLayout ? (
            <colgroup>
              {columns.map(column => (
                <col key={column.key} style={{ width: column.width }} />
              ))}
            </colgroup>
          ) : null}
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              {columns.map((column, colIndex) => (
                <th
                  key={column.key}
                  style={{
                    width: column.width,
                    minWidth: column.width,
                    ...stickyColStyle(colIndex, column),
                  }}
                  onClick={() => handleSortToggle(column)}
                  className={`table-sticky-header px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200 ${
                    fixedLayout
                      ? "overflow-hidden whitespace-normal"
                      : "whitespace-nowrap"
                  } ${stickyColClass(colIndex)} ${stickyColHeaderClass(
                    colIndex
                  )} ${column.className || ""} ${
                    column.textalign === "center"
                      ? "text-center"
                      : column.textalign === "right"
                        ? "text-right"
                        : "text-left"
                  } ${sortable && (column.sorter != null || column.sortable) ? "cursor-pointer select-none" : ""}`}
                >
                  <div
                    className={`flex items-center gap-2 ${
                      column.textalign === "center"
                        ? "justify-center"
                        : column.textalign === "right"
                          ? "justify-end"
                          : ""
                    }`}
                  >
                    <span
                      className={`block w-full ${
                        fixedLayout
                          ? "whitespace-normal break-words"
                          : "whitespace-nowrap"
                      }`}
                    >
                      {column.title}
                    </span>
                    {renderSortIcon(column)}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          {renderTableBody()}
        </table>
      </div>
      {pagination && (
        <Pagination
          mode="static"
          currentPage={currentPage}
          pageSize={rowsPerPage}
          dataLength={sortedData.length}
          onPageChange={setCurrentPage}
          onPageSizeChange={handleRowsPerPageChange}
          pageSizeOptions={rowsPerPageOptions}
          className="mt-4"
        />
      )}
    </div>
  );
};

export default Table;
